import { useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import type { ChartData, ChartOptions } from 'chart.js'

import { ensureChartJsRegistered, resolveCanvasColor, withCanvasAlpha, chartJsPdfReadyPlugin, pickCategoricalChartPalette, pickDeckChartPalette } from '@/lib/charts/chartjs'
import { chartTooltipPreset } from '../../chart-tooltip-preset'
import { CHART_DEFAULTS } from '../../../../foundations/constants'
import { CHART_LIMITS } from '../../../../foundations/slide-limits'
import {
  formatGroupedBarTickLabel,
  GROUPED_BAR_TICK_MAX_ROTATION,
} from './formatGroupedBarTickLabel'

export interface GroupedBarSeries {
  label: string
  values: number[]
  color?: string
}

export interface ChartJsGroupedBarProps {
  labels: string[]
  series: GroupedBarSeries[]
  height?: number | string
  /** Category direction. Deck slides retain vertical columns by default. */
  orientation?: 'vertical' | 'horizontal'
  /** Stack the series on the shared value axis. */
  stacked?: boolean
  /** Maximum on the value axis, whichever physical axis carries it. */
  yAxisMax?: number
  yAxisTitle?: string
  /** Show value-axis gridlines. */
  showYGrid?: boolean
  /** Analyst multi-series charts only. Deck slides keep the hidden legend. */
  showLegend?: boolean
  /** Hue-separated series colors. Deck slides keep the single-hue ramp. */
  useCategoricalPalette?: boolean
}

function groupedBarParsedValue(
  item: any,
  orientation: 'vertical' | 'horizontal',
): number | null {
  const parsed = item?.parsed
  if (typeof parsed === 'number' && Number.isFinite(parsed)) {
    return parsed
  }
  if (!parsed || typeof parsed !== 'object') {
    return null
  }
  const value = orientation === 'horizontal' ? parsed.x : parsed.y
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/**
 * @deprecated 2026-10-01: legacy reader only. Do not use in new code; draw the shared
 * plots in `packages/deck-ui/src/plots` instead. Kept so old studies the figure service
 * refuses (and cards whose figure fails) still draw exactly as before.
 * Delete after 2027-01-01, together with the legacy fallback in QuestionCard.
 */
export function ChartJsGroupedBar({
  labels,
  series,
  height = '100%',
  orientation = 'vertical',
  stacked = false,
  yAxisMax = 1,
  yAxisTitle,
  // Deck default: no horizontal gridlines (cleaner, less "dashboard-y").
  showYGrid = false,
  showLegend = false,
  useCategoricalPalette = false,
}: ChartJsGroupedBarProps) {
  ensureChartJsRegistered()

  // Enforce chart limits
  const clampedLabels = useMemo(
    () => labels.slice(0, CHART_LIMITS.groupedBarMaxLabels),
    [labels]
  )
  const clampedSeries = useMemo(
    () => series.slice(0, CHART_LIMITS.groupedBarMaxSeries).map(s => ({
      ...s,
      values: s.values.slice(0, CHART_LIMITS.groupedBarMaxLabels),
    })),
    [series]
  )

  const defaultSeriesColors = useMemo(() => {
    const count = Math.max(1, clampedSeries.length)
    return useCategoricalPalette
      ? pickCategoricalChartPalette(count)
      : pickDeckChartPalette(count)
  }, [clampedSeries.length, useCategoricalPalette])

  const chartData = useMemo(() => {
    return {
      labels: clampedLabels,
      datasets: clampedSeries.map((s, idx) => ({
        label: s.label,
        data: s.values,
        backgroundColor: withCanvasAlpha(
          resolveCanvasColor(s.color || defaultSeriesColors[idx] || 'var(--slide-chart-1)'),
          CHART_DEFAULTS.fillAlpha
        ),
        borderWidth: 0,
        barPercentage: 0.6,
        categoryPercentage: 0.8,
      })),
    } satisfies ChartData<'bar'>
  }, [clampedLabels, clampedSeries, defaultSeriesColors])

  const options: ChartOptions<'bar'> = useMemo(
    () => {
      const isPercentAxis = yAxisMax === 100
      const categoryScale = {
        type: 'category' as const,
        stacked,
        grid: { display: false },
        ticks: {
          color: 'var(--slide-text-primary)',
          font: { size: CHART_DEFAULTS.tickFontSize.md, weight: 'bold' as const },
          minRotation: 0,
          maxRotation: orientation === 'vertical' ? GROUPED_BAR_TICK_MAX_ROTATION : 0,
          callback: (tickValue: string | number) => {
            const index = Number(tickValue)
            const label = Number.isFinite(index)
              ? clampedLabels[index] ?? String(tickValue)
              : String(tickValue)
            return formatGroupedBarTickLabel(label) as string
          },
        },
      }
      const valueScale = {
        type: 'linear' as const,
        stacked,
        beginAtZero: true,
        max: yAxisMax,
        title: yAxisTitle
          ? {
              display: true,
              text: yAxisTitle,
              font: { weight: 'bold' as const },
              color: 'var(--slide-text-primary)',
            }
          : undefined,
        border: {
          display: true,
          color: withCanvasAlpha(resolveCanvasColor('var(--slide-border-soft)', 'var(--slide-border-soft)'), 0.8),
        },
        grid: showYGrid
          ? {
              display: true,
              color: withCanvasAlpha(resolveCanvasColor('var(--slide-border-soft)', 'var(--slide-border-soft)'), 0.25),
            }
          : { display: false },
        ticks: {
          color: 'var(--slide-text-muted)',
          font: { size: CHART_DEFAULTS.tickFontSize.sm },
          ...(isPercentAxis
            ? {
                callback: (tickValue: string | number) => {
                  const n = Number(tickValue)
                  return Number.isFinite(n) ? `${n}%` : String(tickValue)
                },
              }
            : {}),
        },
      }

      return {
        indexAxis: orientation === 'horizontal' ? 'y' as const : 'x' as const,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: showLegend
            ? {
                display: clampedSeries.length >= 2,
                position: 'bottom' as const,
                labels: {
                  usePointStyle: true,
                  color: resolveCanvasColor('var(--slide-text-primary)', 'var(--ds-text-primary)'),
                  font: { size: CHART_DEFAULTS.tickFontSize.md, weight: 500 as const },
                  padding: 16,
                },
              }
            : { display: false },
          // Grouped bars are multi-series: the title row (the shared category)
          // is meaningful and distinct from each per-series body row.
          tooltip: chartTooltipPreset({
            dropTitle: false,
            ...(isPercentAxis
              ? {
                  valueText: (item: any) => {
                    const value = groupedBarParsedValue(item, orientation)
                    return value == null ? '' : `${Math.round(value)}%`
                  },
                }
              : {}),
          }),
        },
        scales: orientation === 'horizontal'
          ? { x: valueScale, y: categoryScale }
          : { x: categoryScale, y: valueScale },
      }
    },
    [clampedLabels, clampedSeries.length, orientation, stacked, yAxisMax, yAxisTitle, showYGrid, showLegend]
  )

  const resolvedHeight = typeof height === 'number' ? `${height}px` : height

  return (
    <div style={{ position: 'relative', width: '100%', height: resolvedHeight, minHeight: 0 }} data-chart-library="chartjs">
      <Bar data={chartData} options={options} plugins={[chartJsPdfReadyPlugin]} />
    </div>
  )
}
